import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Shield, User, Edit, ShoppingCart, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    acesso_vendas: false
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    acesso_vendas: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Determinar a role com base nas seleções
      let finalRole = formData.role; // 'admin' ou 'user'
      if (formData.role === 'user' && formData.acesso_vendas) {
        finalRole = 'vendedor'; // Não autorizado no principal + acesso vendas = vendedor
      }
      
      await axios.post(`${API}/users`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: finalRole,
        acesso_vendas: formData.acesso_vendas
      });
      toast.success('Usuário criado com sucesso!');
      setDialogOpen(false);
      fetchUsers();
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        acesso_vendas: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Determinar o role do sistema principal
    const isPrincipalAdmin = user.role === 'admin';
    const hasVendasAccess = user.role === 'vendedor' || user.role === 'admin' || user.acesso_vendas === true;
    
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: isPrincipalAdmin ? 'admin' : 'user',
      acesso_vendas: hasVendasAccess
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Determinar a role final
      let finalRole = editFormData.role; // 'admin' ou 'user'
      if (editFormData.role === 'user' && editFormData.acesso_vendas) {
        finalRole = 'vendedor';
      }
      
      const dataToSend = {
        name: editFormData.name,
        email: editFormData.email,
        role: finalRole,
        acesso_vendas: editFormData.acesso_vendas
      };
      
      // Só envia a senha se foi preenchida
      if (editFormData.password && editFormData.password.trim()) {
        dataToSend.password = editFormData.password;
      }

      await axios.put(`${API}/users/${editingUser.id}`, dataToSend);
      toast.success('Usuário atualizado com sucesso!');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role === 'user').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Usuários</h1>
            <p className="text-muted-foreground mt-1">Gerencie os usuários do sistema</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                <Plus className="h-4 w-4 mr-2" />
                Novo Usuário
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope">Novo Usuário</DialogTitle>
                <DialogDescription>Adicione um novo usuário ao sistema</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                  />
                </div>

                {/* Seção: Sistema Principal */}
                <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Building2 className="h-4 w-4 text-primary" />
                    Sistema Principal
                  </div>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Não Autorizado</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Administradores têm acesso completo ao sistema principal.
                  </p>
                </div>

                {/* Seção: Sistema de Vendas */}
                <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ShoppingCart className="h-4 w-4 text-amber-600" />
                    Sistema de Vendas
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="acesso-vendas" className="text-sm cursor-pointer">
                      Permitir acesso ao módulo de vendas
                    </Label>
                    <Switch
                      id="acesso-vendas"
                      checked={formData.acesso_vendas}
                      onCheckedChange={(checked) => setFormData({ ...formData, acesso_vendas: checked })}
                      data-testid="acesso-vendas-switch"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.acesso_vendas 
                      ? 'O usuário poderá acessar o sistema de vendas (/vendas).' 
                      : 'O usuário não terá acesso ao sistema de vendas.'}
                  </p>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-user">
                  Criar Usuário
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total de Usuários</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono text-foreground" data-testid="total-users">
                {users.length}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Administradores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono text-primary" data-testid="admin-count">
                {adminCount}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Usuários Comuns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono text-foreground" data-testid="user-count">
                {userCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-manrope">Lista de Usuários ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Nome</th>
                    <th className="text-left p-3 hidden sm:table-cell">Email</th>
                    <th className="text-left p-3">Sistema Principal</th>
                    <th className="text-left p-3">Vendas</th>
                    <th className="text-left p-3 hidden md:table-cell">Cadastro</th>
                    <th className="text-center p-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map(user => {
                    const isPrincipalAdmin = user.role === 'admin';
                    const hasVendasAccess = user.role === 'vendedor' || user.role === 'admin' || user.acesso_vendas === true;
                    
                    return (
                    <tr key={user.id} className="hover:bg-muted/50 transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            {isPrincipalAdmin ? (
                              <Shield className="h-4 w-4 text-primary" />
                            ) : (
                              <User className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <span className="font-medium block">{user.name}</span>
                            <span className="text-xs text-muted-foreground sm:hidden">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground hidden sm:table-cell">{user.email}</td>
                      <td className="p-3">
                        <Badge
                          variant={isPrincipalAdmin ? 'default' : 'secondary'}
                          className={isPrincipalAdmin ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-600'}
                        >
                          {isPrincipalAdmin ? 'Admin' : 'Não Autor.'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge
                          variant={hasVendasAccess ? 'default' : 'secondary'}
                          className={hasVendasAccess ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}
                        >
                          {hasVendasAccess ? 'Sim' : 'Não'}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground hidden md:table-cell">
                        {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(user)}
                            data-testid={`edit-user-${user.id}`}
                            title="Editar usuário"
                          >
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user.id)}
                            data-testid={`delete-user-${user.id}`}
                            title="Excluir usuário"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Editar Usuário
              </DialogTitle>
              <DialogDescription>Atualize os dados do usuário</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  placeholder="Nome completo"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  data-testid="edit-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                  data-testid="edit-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-password">Nova Senha</Label>
                <Input
                  id="edit-password"
                  type="password"
                  placeholder="Deixe em branco para manter a atual"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  data-testid="edit-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  Deixe em branco para manter a senha atual
                </p>
              </div>

              {/* Seção: Sistema Principal */}
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Building2 className="h-4 w-4 text-primary" />
                  Sistema Principal
                </div>
                <Select value={editFormData.role} onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}>
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Não Autorizado</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Administradores têm acesso completo ao sistema principal.
                </p>
              </div>

              {/* Seção: Sistema de Vendas */}
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ShoppingCart className="h-4 w-4 text-amber-600" />
                  Sistema de Vendas
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-acesso-vendas" className="text-sm cursor-pointer">
                    Permitir acesso ao módulo de vendas
                  </Label>
                  <Switch
                    id="edit-acesso-vendas"
                    checked={editFormData.acesso_vendas}
                    onCheckedChange={(checked) => setEditFormData({ ...editFormData, acesso_vendas: checked })}
                    data-testid="edit-acesso-vendas-switch"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {editFormData.acesso_vendas 
                    ? 'O usuário poderá acessar o sistema de vendas (/vendas).' 
                    : 'O usuário não terá acesso ao sistema de vendas.'}
                </p>
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" data-testid="submit-edit-user">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
